import { rd } from '@passionware/monads';
import { injectConfig } from '@passionware/platform-react';
import { createMatcher } from '@passionware/query-toolkit';
import type { ReactElement } from 'react';
import type {
  EnterpriseListFilter,
  EnterpriseWorkspace
} from 'v5/api/enterprise/enterprise.api';
import { ItemSelector } from 'v5/patterns/widgets/ItemSelector';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

const EnterpriseWorkspacePickerInner = injectConfig(
  ItemSelector<EnterpriseWorkspace>
)
  .fromProps<
    WithServices<[WithEnterpriseService]> & {
      organizationId: string;
      filter?: EnterpriseListFilter;
      disabledWorkspaceId?: number | null;
    }
  >(api => ({
    useOptions: (query, isDropdownOpen) => {
      const {
        organizationId,
        filter = 'active',
        services,
        disabledWorkspaceId = null
      } = api.useProps();
      const workspacesRd = services.enterpriseService.useWorkspaces(
        isDropdownOpen ? organizationId : null,
        filter
      );
      return rd.map(workspacesRd, workspaces =>
        workspaces
          .filter(createMatcher(query, workspace => workspace.name))
          .map(workspace => ({
            id: workspace.id.toString(),
            label: workspace.name,
            disabled:
              disabledWorkspaceId != null &&
              workspace.id === disabledWorkspaceId,
            content: (
              <div className="flex items-center gap-2">
                <EnterpriseWorkspaceIcon
                  name={workspace.name}
                  iconUrl={workspace.iconUrl}
                />
                <span>{workspace.name}</span>
              </div>
            ),
            extra: workspace
          }))
      );
    }
  }))
  .transformProps(x => x.passAll);

export type EnterpriseWorkspacePickerProps = WithServices<
  [WithEnterpriseService]
> & {
  organizationId: string;
  filter?: EnterpriseListFilter;
  disabledWorkspaceId?: number | null;
  onWorkspaceSelect: (workspace: EnterpriseWorkspace) => void;
  children: ReactElement;
  popoverClassName?: string;
};

export function EnterpriseWorkspacePicker({
  organizationId,
  filter,
  disabledWorkspaceId,
  onWorkspaceSelect,
  children,
  popoverClassName,
  services
}: EnterpriseWorkspacePickerProps) {
  return (
    <EnterpriseWorkspacePickerInner
      services={services}
      organizationId={organizationId}
      filter={filter}
      disabledWorkspaceId={disabledWorkspaceId}
      popoverClassName={popoverClassName}
      onSelect={(id, _wasChecked, workspace) => onWorkspaceSelect(workspace)}
    >
      {children}
    </EnterpriseWorkspacePickerInner>
  );
}
